import React from 'react';
import { Container, Typography, Box, Paper, Divider, useTheme, useMediaQuery } from '@mui/material';

const PrivacyPolicyPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.125rem' } }}
          >
            Privacy Policy
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 3, md: 4 },
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }
            }}
          >
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            roamzap("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
            This privacy policy explains how we collect, use, and safeguard your information when you use our website 
            and services.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Information We Collect
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We may collect personal information that you voluntarily provide to us when using our services, including 
            but not limited to your name, email address, travel preferences, and travel history.
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We also automatically collect certain information when you visit our website, including your IP address, 
            browser type, operating system, referring website, pages visited, and the dates/times of visits.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            How We Use Your Information
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We use the information we collect to:
          </Typography>
          
          <ul style={{ paddingLeft: isMobile ? '20px' : '40px' }}>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                Provide, operate, and maintain our services
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                Improve, personalize, and expand our services
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                Understand and analyze how you use our services
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                Develop new products, services, features, and functionality
              </Typography>
            </li>
            <li>
              <Typography 
                paragraph
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  mt: 0.5,
                  mb: { xs: 1, md: 2 }
                }}
              >
                Communicate with you about our services, updates, and other information
              </Typography>
            </li>
          </ul>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Data Security
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We implement appropriate security measures designed to protect your personal information. 
            However, no method of transmission over the Internet or electronic storage is 100% secure, 
            so we cannot guarantee absolute security.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Third-Party Services
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We may use third-party services to help us operate our website and services. 
            These third parties have access to your personal information only to perform 
            specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.
          </Typography>
          
          <Typography 
            variant="h6" 
            gutterBottom 
            fontWeight="bold" 
            sx={{ 
              mt: { xs: 3, md: 4 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' }
            }}
          >
            Changes to This Privacy Policy
          </Typography>
          
          <Typography 
            paragraph
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            We may update this privacy policy from time to time. We will notify you of any changes by 
            posting the new privacy policy on this page. You are advised to review this privacy policy 
            periodically for any changes.
          </Typography>
          
          <Divider sx={{ my: { xs: 3, md: 4 } }} />
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage; 